import { secureHttpClient } from '../restClient';
import { useGetConsumer } from './flowManager';
import logger from './loggerManager';
import { useGetStoreId } from './storeManager';
import {
  useGetAdvancedCartDetails,
  useGetCartTypeToggle,
  useGetCart,
  useGetMerchantOrderId,
} from './cartManager';
import {
  appDataByMerchant,
  AppOptions,
  SettleOptions,
  useGetInteractionOptions,
  useGetMerchantCurrency,
  useGetSelectedSettleOption,
} from './configManager';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import {
  CartDetails,
  CartItem,
  ConsumerDetails,
} from '@chargeafter/payment-sdk';
import { getFlags } from '@chargeafter/feature-flag';

export enum LinkFlow {
  Apply = 'apply',
  Checkout = 'checkout',
  ExpressCheckout = 'expressCheckout',
}

export enum LinkDeliveryMethod {
  Email = 'email',
  Sms = 'sms',
  QrCode = 'qrCode',
}
export interface LinkInteraction {
  action: LinkFlow;
  deliveryMethod: LinkDeliveryMethod;
}

interface SendLinkResponse {
  success: boolean;
  error?: string | undefined;
  url?: string;
}

interface OmniLinkResponse {
  url: string;
  linkId: string;
}

type SendLinkData = Partial<ConsumerDetails> & {
  autoSettle?: Omit<SettleOptions, 'AutomaticAndManual'>;
  deliveryMethod: LinkDeliveryMethod;
};

interface CommonLinkData {
  flow: 'apply' | 'checkout';
  storeId: string;
  currency: string;
  consumer: ConsumerDetails;
  cart: CartDetails;
  merchantOrderId?: string;
  charge: boolean;
  settle: boolean;
  deliveryMethod: LinkDeliveryMethod;
}

interface SendLinkPayload {
  channel: string;
  posType: string;
  flow: 'checkout' | 'apply';
  consumerDetails: ConsumerDetails;
  sendUsing: 'email' | 'redirectUrl';
  storeId: string;
  currency: string;
  cartDetails?: CartDetails;
  merchantOrderId?: string;
  createCharge?: boolean;
  autoSettle?: boolean;
}
interface OmniLinkCart {
  items: CartItem[];
  totalAmount: number;
  totalTaxAmount: number;
  shippingAmount: number;
  merchantOrderId?: string;
  discounts?: CartDetails['discounts'];
  currency: string;
}

interface OmniLinkPayload {
  consumer: ConsumerDetails;
  applicationDetails: {
    correlationId?: string;
    tags?: Record<string, string>;
    requestedLoanAmount?: number;
    currency?: string;
    channel: string;
  };
  merchantDetails: {
    storeId?: string;
    posType: 'GenericConsumerDevice';
    posId?: string;
  };
  linkOptions: {
    linkFormat: 'Url';
    deliveryMethod: 'Email' | 'Sms' | 'None';
    onSuccessRedirectUrl?: string;
    onFailureRedirectUrl?: string;
  };
  cart?: OmniLinkCart;
  checkoutOptions?: {
    charge: boolean;
    settle: boolean;
  };
}
type InteractionSuffixs = 'Link' | 'QrCode' | 'Sms';
const mapInteractionOptionSuffixToDeliveryMethod = (
  suffix: InteractionSuffixs,
) => {
  switch (suffix) {
    case 'Link':
      return LinkDeliveryMethod.Email;
    case 'QrCode':
      return LinkDeliveryMethod.QrCode;
    case 'Sms':
      return LinkDeliveryMethod.Sms;
  }
};

const getInitialLinkInteraction = (
  interactions: AppOptions['interactionOptions'],
): [LinkFlow, LinkDeliveryMethod] => {
  const actions = ['apply', 'checkout', 'expressCheckout'];
  const deliveryMethods: InteractionSuffixs[] = ['Link', 'QrCode', 'Sms'];

  for (const action of actions) {
    for (const deliveryMethod of deliveryMethods) {
      const key =
        `${action}${deliveryMethod}` as keyof AppOptions['interactionOptions'];
      if (interactions[key]) {
        return [
          action as LinkFlow,
          mapInteractionOptionSuffixToDeliveryMethod(deliveryMethod),
        ];
      }
    }
  }
  return [LinkFlow.Apply, LinkDeliveryMethod.Email];
};

export const getInitialDeliveryMethodByFlow = (
  flow: LinkFlow,
  interactions: AppOptions['interactionOptions'],
): LinkDeliveryMethod => {
  const deliveryMethods: InteractionSuffixs[] = ['Link', 'QrCode', 'Sms'];
  for (const deliveryMethod of deliveryMethods) {
    const key =
      `${flow}${deliveryMethod}` as keyof AppOptions['interactionOptions'];
    if (interactions[key]) {
      return mapInteractionOptionSuffixToDeliveryMethod(deliveryMethod);
    }
  }
  return LinkDeliveryMethod.Email;
};

const currentLinkInteractionAtom = atom<LinkInteraction | null>(null);
const LinkInteractionAtom = atom(
  async (get) => {
    const currentInteraction = get(currentLinkInteractionAtom);
    if (currentInteraction) return currentInteraction;

    const merchantConfiguration = await get(appDataByMerchant);
    const [action, deliveryMethod] = getInitialLinkInteraction(
      merchantConfiguration.configuration.options.interactionOptions,
    );

    return {
      action,
      deliveryMethod,
    };
  },
  (_, set, v: LinkInteraction) => {
    set(currentLinkInteractionAtom, v);
  },
);

export const useGetLinkInteraction = () => useAtomValue(LinkInteractionAtom);
export const useSetLinkInteraction = () => useSetAtom(LinkInteractionAtom);
export const useResetLinkInteraction = () => {
  const setLinkInteraction = useSetLinkInteraction();
  const merchantInteractions = useGetInteractionOptions();
  const [initialAction, initialDeliveryMethod] =
    getInitialLinkInteraction(merchantInteractions);
  return () => {
    setLinkInteraction({
      action: initialAction,
      deliveryMethod: initialDeliveryMethod,
    });
  };
};

const useOmniLinkNew = () => {
  return async (data: CommonLinkData) => {
    const requestBody = buildOmniLinkPayload(data);

    logger.log(`Send ${data.flow} via link request`, {
      filename: 'sendLinkManager.ts',
      function: 'useOmniLinkNew',
      body: requestBody,
    });

    const response = await secureHttpClient.sendOmniLink<OmniLinkResponse>(
      { fileName: 'sendLinkManager.ts', fetchingFunction: 'useSendLink' },
      requestBody,
      data.flow === LinkFlow.Apply ? 'apply' : 'checkout',
    );

    logger.log(`Received ${data.flow} via link response`, {
      filename: 'sendLinkManager.ts',
      function: 'useOmniLinkNew',
      resultData: response,
    });

    //TODO: return response as is when we'll use omni only
    return { success: true, url: (response as OmniLinkResponse)?.url };
  };
};

const useOmniChannelOld = () => {
  return async (data: CommonLinkData) => {
    const requestBody = buildSendLinkPayload(data);

    logger.log(`Send ${data.flow} via link request`, {
      filename: 'sendLinkManager.ts',
      function: 'useOmniChannelOld',
      body: requestBody,
    });

    const response = await secureHttpClient.sendLink(
      { fileName: 'sendLinkManager.ts', fetchingFunction: 'useSendLink' },
      requestBody,
    );

    logger.log(`Received ${data.flow} via link response`, {
      filename: 'sendLinkManager.ts',
      function: 'useOmniChannelOld',
      resultData: response,
    });

    return response;
  };
};

export const useSendLink = () => {
  const cartDetails = useGetCart();
  const advancedCart = useGetAdvancedCartDetails();
  const consumer = useGetConsumer();
  const linkAction = useAtomValue(LinkInteractionAtom).action;
  const isCheckout = linkAction !== LinkFlow.Apply;
  const merchantOrderId = useGetMerchantOrderId();
  const storeId = useGetStoreId();
  const isAdvancedCart = useGetCartTypeToggle();
  const selectedSettleOption = useGetSelectedSettleOption();
  const currency = useGetMerchantCurrency();
  const sendLinkNew = useOmniLinkNew();
  const sendLinkOld = useOmniChannelOld();
  return async ({
    autoSettle,
    deliveryMethod = LinkDeliveryMethod.Email,
    ...consumerBasicData
  }: SendLinkData): Promise<SendLinkResponse> => {
    const { ffSalesAppShouldUseOmniLink } = getFlags();

    try {
      const consumerCommunicationDetails = {
        firstName: consumerBasicData.firstName,
        lastName: consumerBasicData.lastName,
        ...(deliveryMethod === LinkDeliveryMethod.Email && {
          email: consumerBasicData.email,
        }),
        ...(deliveryMethod === LinkDeliveryMethod.Sms && {
          mobilePhoneNumber: consumerBasicData.mobilePhoneNumber,
        }),
      };

      const consumerDetails =
        linkAction === LinkFlow.Apply
          ? consumerCommunicationDetails
          : {
              ...consumer,
              ...consumerCommunicationDetails,
            };

      const linksData: CommonLinkData = {
        consumer: consumerDetails,
        cart: cartDetails,
        merchantOrderId: isAdvancedCart
          ? advancedCart.merchantOrderId
          : merchantOrderId,
        charge: isCheckout,
        settle:
          selectedSettleOption === 'Automatic' || autoSettle === 'Automatic',
        currency,
        deliveryMethod,
        flow: linkAction === LinkFlow.Apply ? 'apply' : 'checkout',
        storeId,
      };

      return ffSalesAppShouldUseOmniLink
        ? await sendLinkNew(linksData)
        : await sendLinkOld(linksData);
    } catch (e) {
      const ex = e as { code: string };
      logger.error(
        `${linkAction} via link exception occurred: ${JSON.stringify(e)}`,
        {
          filename: 'flowManager.ts',
          function: 'useSendLink',
        },
      );
      return { success: false, ...(ex.code && { error: ex.code }) };
    }
  };
};

const buildSendLinkPayload = (data: CommonLinkData): SendLinkPayload => {
  const isCheckout = data.flow !== LinkFlow.Apply;

  return {
    channel: 'in_store',
    posType: 'GenericConsumerDevice',
    flow: isCheckout ? 'checkout' : 'apply',
    consumerDetails: data.consumer,
    sendUsing: mapSendLinkDeliveryMethod(data.deliveryMethod),
    storeId: data.storeId,
    currency: data.currency,
    ...(isCheckout && {
      cartDetails: data.cart,
      merchantOrderId: data.merchantOrderId,
      createCharge: data.charge,
      autoSettle: data.settle,
    }),
  };
};

const buildOmniLinkPayload = (data: CommonLinkData): OmniLinkPayload => {
  return {
    consumer: data.consumer,
    applicationDetails: {
      currency: data.currency,
      channel: 'IN_STORE',
    },
    merchantDetails: {
      storeId: data.storeId,
      posType: 'GenericConsumerDevice',
    },
    linkOptions: {
      linkFormat: 'Url',
      deliveryMethod: mapOmniLinkDeliveryMethod(data.deliveryMethod),
    },

    ...(data.flow !== LinkFlow.Apply && {
      cart: {
        ...data.cart,
        merchantOrderId: data.merchantOrderId,
        totalTaxAmount: data.cart.taxAmount,
        currency: data.currency,
      },
      checkoutOptions: {
        charge: data.charge,
        settle: data.settle,
      },
    }),
  };
};

const mapSendLinkDeliveryMethod = (method: LinkDeliveryMethod) => {
  switch (method) {
    case LinkDeliveryMethod.Email:
      return 'email';
    case LinkDeliveryMethod.QrCode:
      return 'redirectUrl';
    default:
      throw new Error('Method not supported');
  }
};

const mapOmniLinkDeliveryMethod = (method: LinkDeliveryMethod) => {
  switch (method) {
    case LinkDeliveryMethod.Email:
      return 'Email';
    case LinkDeliveryMethod.QrCode:
      return 'None';
    case LinkDeliveryMethod.Sms:
      return 'Sms';
    default:
      throw new Error('Method not supported');
  }
};
